import React, { useState, useEffect, memo } from "react";
import { connect } from "react-redux";
import { useParams, Link } from "react-router";
import { Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  faCircleInfo,
  faEdit,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ThreeDots } from "react-loader-spinner";

import {
  brandProfileConstants,
  emptyString,
  groupsConstants,
  loginConstants,
  pathConstant,
  systemGreenColor,
  tabsNames,
  viewPathConstants,
  screenSize,
  modalSize,
  EMPTY_ARRAY_LENGTH,
  BRAND_GROUPS_LABELS,
  PAGINATION_INDEXING,
} from "../../constants";
import { setItemToStorage } from "../../../../system";
import {
  useSpinner,
  Spinner,
  StatusIndicator,
  ToggleSwitch,
  addFranchisePreviewModal,
  getConfirmationModal,
  isSuperAdmin,
  isSuperOrBrandAdmin,
  handleAsyncAction,
} from "../../../../shared";
import { groupsActions } from "../../redux/actions";
import {
  advanceListPaginationQuery,
  hasMoreListItems,
  resetListPaginationFrom,
} from "../shared/listPaginationUtility";

const { Body, Item, Header } = Accordion;

const GroupsTab = (props) => {
  const {
    groups,
    activeTab,
    setGroups,
    queryString,
    updateGroup,
    setQueryString,
    getGroupsByBrandId,
    updateGroupResponse,
    groupsReducer: { allGroups, groupUpdateResponse },
  } = props;

  const { brandId } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [modalElement, setModalElement] = useState(null);

  const groupsCount = allGroups?.length ?? EMPTY_ARRAY_LENGTH;

  // Clear the handled group response once for the whole list.
  useEffect(() => {
    if (groupUpdateResponse === groupsConstants.CREATE_SYSTEM_USER) {
      updateGroupResponse();
    }
  }, [groupUpdateResponse, updateGroupResponse]);

  //To open the modal
  const openModal = () => {
    setShowModal(true);
  };

  //To close the modal
  const closeModal = () => {
    setShowModal(false);
  };

  //Setting modal component
  const setComponent = (component) => {
    setModalElement(component);
    openModal();
  };

  //Setting the amount of groups already fetched
  //istanbul ignore next
  const getGroupsData = () => {
    //istanbul ignore next
    setQueryString((prevQueryString) =>
      advanceListPaginationQuery(prevQueryString, groups.length)
    );
  };

  //Modal to update the group
  const updateGroupDetails = (e, group) => {
    e.stopPropagation();
    setComponent(
      addFranchisePreviewModal({
        onClose: closeModal,
        row: group,
        editMode: true,
        brandId: brandId,
        queryString: queryString,
      })
    );
  };

  //Modal to delete the group
  const deleteGroup = (e, groupId, groupName) => {
    e.stopPropagation();
    setComponent(
      getConfirmationModal({
        onClose: closeModal,
        Title: BRAND_GROUPS_LABELS.CONFIRM,
        message: `Are you sure you want to delete ${groupName}?`,
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        onConfirm: () =>
          handleAsyncAction(
            props.deleteGroup(
              groupId,
              resetListPaginationFrom(queryString),
              brandId,
            ),
            { onSuccess: closeModal },
          ),
      })
    );
  };

  //Modal to remove the brand location
  const removeBrandLocation = (e, brandLocationId, brandLocationName) => {
    e.stopPropagation();
    setComponent(
      getConfirmationModal({
        onClose: closeModal,
        Title: BRAND_GROUPS_LABELS.CONFIRM,
        message: `Are you sure you want to remove ${brandLocationName}?`,
        ModalSize: modalSize.MODAL_SIZE_MEDIUM,
        onConfirm: () =>
          handleAsyncAction(
            props.removeBrandLocation(
              brandLocationId,
              resetListPaginationFrom(queryString),
              brandId,
            ),
            { onSuccess: closeModal },
          ),
      })
    );
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  //Handle store click
  const handleClickStoreName = (location) => {
    setItemToStorage(loginConstants.LOCATION_ID, location.id);
    setItemToStorage(
      brandProfileConstants.LOCATION_STATE,
      JSON.stringify({
        name: isSuperAdmin() ? pathConstant.STORE_GROUP : pathConstant.GROUP,
      })
    );
  };

  //Update all group state if it has changes
  useEffect(() => {
    setGroups(allGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups]);

  //Get all groups if change in query string
  useEffect(() => {
    if (activeTab === tabsNames.group) {
      getGroupsByBrandId(queryString, brandId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString]);

  return (
    <>
      {/*Modal here */}
      {showModal && modalElement}
      {/*End Modal */}
      {spinner(groupsConstants.UPDATE_GROUP) && <Spinner />}
      <InfiniteScroll
        dataLength={groupsCount}
        next={getGroupsData}
        hasMore={hasMoreListItems(groupsCount)}
        loader={
          spinner(groupsConstants.GROUPS_DATA) ? (
            <ThreeDots ariaLabel="loading-indicator" />
          ) : (
            emptyString
          )
        }
        scrollableTarget={
          window.innerWidth < screenSize.LAYOUT_DESKTOP
            ? emptyString
            : "main-container"
        }
      >
        <div className="vertical-scroll" aria-label="groups-tab">
          <div className="row group-header m-0">
            <div className="col-4">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="group-name-header"
              >
                {BRAND_GROUPS_LABELS.GROUP_NAME}
              </p>
            </div>
            <div className="col-3">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="store-count-header"
              >
                {BRAND_GROUPS_LABELS.STORES}
              </p>
            </div>
            <div className="col-3">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="group-status-header"
              >
                {BRAND_GROUPS_LABELS.STATUS}
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip
                      id={`tooltip-receive-all-responses`}
                      className="tooltip-style"
                    >
                      {BRAND_GROUPS_LABELS.STATUS_TOOLTIP}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className="info-icon pb-0"
                  />
                </OverlayTrigger>
              </p>
            </div>
            <div className="col-2">
              <p
                className="font-12-px bold-6 p-2 m-0"
                aria-label="group-actions-header"
              >
                {BRAND_GROUPS_LABELS.ACTIONS}
              </p>
            </div>
          </div>
          <Accordion>
            {groups.length > 0 ? (
              groups.map((group, index) => (
                <Item
                  key={group?.id}
                  eventKey={index}
                  className="store-accordion pb-0"
                  aria-label="group-accordion"
                >
                  <Header
                    className="accordion-header"
                    aria-label="group-accordion-header"
                  >
                    <div className="row w-100">
                      <div className="col-4 text-break font-14-px d-flex align-items-center">
                        <p data-testid="group-name-text" className="p-2 m-0">
                          {group?.name}
                        </p>
                      </div>
                      <div className="col-3 font-14-px d-flex align-items-center">
                        <p className="p-2 m-0">{group?.storesList?.length}</p>
                      </div>
                      <div
                        data-testid="toggle-switch"
                        className="col-3 font-14-px d-flex align-items-center"
                      >
                        {isSuperAdmin() ? (
                          <ToggleSwitch
                            isActive={group?.isActive}
                            onChange={() => {
                              updateGroup(
                                group?.id,
                                {
                                  ...queryString,
                                  from: PAGINATION_INDEXING.FIRST_PAGE_INDEX,
                                  take: groups.length,
                                },
                                brandId
                              );
                            }}
                            disabled={!group.brandIsActive}
                          />
                        ) : (
                          <div className="ps-2">
                            <StatusIndicator isActive={group?.isActive} />
                          </div>
                        )}
                      </div>
                      <div className="col-2 d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={faEdit}
                          role="button"
                          aria-hidden="true"
                          aria-label="edit-group-icon"
                          className="fs-4 ms-1 pe-2 p-2"
                          onClick={(e) => updateGroupDetails(e, group)}
                        />
                        <FontAwesomeIcon
                          icon={faTimesCircle}
                          aria-label="delete-group-icon"
                          onClick={(e) => deleteGroup(e, group.id, group.name)}
                          className="text-danger fs-4 ms-1 pe-2 remove-button p-2"
                        />
                      </div>
                    </div>
                  </Header>
                  <Body className="p-2" aria-label="group-accordion-body">
                    {group?.storesList?.length > 0 ? (
                      <div className="locations-container sidebar-green font-14-px">
                        {group.storesList.map((location) => (
                          <div
                            key={location?.id}
                            aria-label="store"
                            className="row item_direction"
                          >
                            <div className="col-8 pl-2 font-14-px d-flex align-items-center p_55">
                              <Link
                                className={`table-links`}
                                to={`${
                                  isSuperOrBrandAdmin()
                                    ? viewPathConstants.STORES
                                    : emptyString
                                }/brand-groups/${brandId}/store-performance/${
                                  location.id
                                }`}
                                state={{
                                  name: isSuperAdmin()
                                    ? pathConstant.STORE_GROUP
                                    : pathConstant.GROUP,
                                }}
                                onClick={() => handleClickStoreName(location)}
                              >
                                {location.name}
                              </Link>
                            </div>
                            <div className="col-4 d-flex align-items-center">
                              {group?.storesList?.length > 1 && (
                                <FontAwesomeIcon
                                  icon={faTimesCircle}
                                  aria-label="delete-store-icon"
                                  className="text-danger fs-4 ms-1 pe-2 remove-button p-2"
                                  onClick={(e) =>
                                    removeBrandLocation(
                                      e,
                                      location.id,
                                      location.name
                                    )
                                  }
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      BRAND_GROUPS_LABELS.NO_LOCATIONS
                    )}
                  </Body>
                </Item>
              ))
            ) : (
              <p className="text-center d-flex justify-content-center align-items-center">
                {spinner(groupsConstants.GROUPS_DATA) ? (
                  <ThreeDots
                    ariaLabel="loading-indicator"
                    color={systemGreenColor}
                  />
                ) : (
                  <>{BRAND_GROUPS_LABELS.NO_GROUP_FOUND}</>
                )}
              </p>
            )}
          </Accordion>
        </div>
      </InfiniteScroll>
    </>
  );
};
const mapStateToProps = (state) => ({
  groupsReducer: state.groupsReducer,
  managersReducer: state.managersReducer,
});
const mapDispatchToProps = (dispatch) => ({
  updateGroupResponse: () => dispatch(groupsActions.updateGroupResponse()),
  getGroupsByBrandId: (queryString, brandId) =>
    dispatch(groupsActions.getGroupsByBrandId(queryString, brandId)),
  deleteGroup: (groupId, queryString, brandId) =>
    dispatch(groupsActions.deleteGroup(groupId, queryString, brandId)),
  removeBrandLocation: (brandLocationId, queryString, brandId) =>
    dispatch(
      groupsActions.removeBrandLocation(brandLocationId, queryString, brandId),
    ),
  updateGroup: (id, body, brandId) =>
    dispatch(groupsActions.updateGroup(id, body, brandId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(GroupsTab));
